import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import type { AxiosError } from 'axios';
import { getFieldErrors, readApiError, DEFAULT_ERROR_MESSAGE } from '@/services/api';
import { useResetPassword } from '@/features/auth/hooks/useResetPassword';
import { buttonClassName } from '@/shared/components/ui/button/buttonClasses';
import { resetPasswordSchema, type ResetPasswordFormInputs } from '@/features/auth/schemas/passwordReset';
import { Form, Input, Label } from '@/shared/components/form';
import { Button } from '@/shared/components/ui/button/Button';

export function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const mutation = useResetPassword();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<ResetPasswordFormInputs>({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: { password: '', password_confirmation: '' },
    });

    if (!token || !email) {
        return (
            <div className="flex flex-col flex-1">
                <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                    <div className="mb-5 sm:mb-8">
                        <h1 className="mb-2 font-semibold text-mp-foreground text-title-sm sm:text-title-md">
                            Invalid Reset Link
                        </h1>
                        <p className="text-sm text-mp-muted">
                            This password reset link is invalid or has expired.
                        </p>
                    </div>
                    <Link
                        to="/password/forgot"
                        className={buttonClassName({ size: 'none', className: 'px-4 py-2 text-sm font-medium' })}
                    >
                        Request a new reset link
                    </Link>
                </div>
            </div>
        );
    }

    const onSubmit = (data: ResetPasswordFormInputs) => {
        mutation.mutate(
            { token, email, password: data.password, password_confirmation: data.password_confirmation },
            {
                onSuccess: () => {
                    toast.success('Password reset successfully.');
                    navigate('/login', { replace: true });
                },
                onError: (err) => {
                    const status = (err as AxiosError).response?.status;
                    const fieldErrors = getFieldErrors(err);

                    if (status === 422 && Object.keys(fieldErrors).length > 0) {
                        for (const [field, messages] of Object.entries(fieldErrors)) {
                            setError(field as keyof ResetPasswordFormInputs, {
                                type: 'server',
                                message: messages[0] ?? 'Invalid',
                            });
                        }
                        return;
                    }

                    const { message: normalisedMessage } = readApiError((err as AxiosError).response?.data);
                    const message = normalisedMessage === DEFAULT_ERROR_MESSAGE
                        ? 'Something went wrong. Please try again.'
                        : normalisedMessage;
                    // Kept verbatim: a product decision about expired reset links, not
                    // error-shape plumbing.
                    if (message.toLowerCase().includes('invalid') || message.toLowerCase().includes('expired')) {
                        toast.error('This reset link has expired or is invalid. Please request a new one.');
                    } else {
                        toast.error(message);
                    }
                },
            },
        );
    };

    return (
        <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
                <div className="mb-5 sm:mb-8">
                    <h1 className="mb-2 font-semibold text-mp-foreground text-title-sm sm:text-title-md">
                        Set New Password
                    </h1>
                    <p className="text-sm text-mp-muted">
                        Enter your new password below.
                    </p>
                </div>

                <Form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-6">
                        <div>
                            <Label>
                                New Password <span className="text-mp-error">*</span>
                            </Label>
                            <div className="relative">
                                <Controller
                                    name="password"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <Input
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            placeholder="Enter new password"
                                            value={field.value ?? ''}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            hint={fieldState.error?.message}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword((v) => !v)}
                                                className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2 text-mp-muted"
                                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            >
                                                {showPassword ? '🙈' : '👁'}
                                            </button>
                                        </Input>
                                    )}
                                />
                            </div>
                        </div>

                        <div>
                            <Label>
                                Confirm Password <span className="text-mp-error">*</span>
                            </Label>
                            <div className="relative">
                                <Controller
                                    name="password_confirmation"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <Input
                                            type={showConfirm ? 'text' : 'password'}
                                            id="password_confirmation"
                                            placeholder="Confirm new password"
                                            value={field.value ?? ''}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            hint={fieldState.error?.message}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirm((v) => !v)}
                                                className="absolute z-30 -translate-y-1/2 cursor-pointer right-4 top-1/2 text-mp-muted"
                                                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                                            >
                                                {showConfirm ? '🙈' : '👁'}
                                            </button>
                                        </Input>
                                    )}
                                />
                            </div>
                        </div>

                        <Button disabled={mutation.isPending} className="w-full" size="sm">
                            {mutation.isPending ? 'Resetting...' : 'Reset Password'}
                        </Button>

                        {errors.root && (
                            <p className="text-sm text-center text-mp-error">{errors.root.message}</p>
                        )}
                    </div>
                </Form>

                <div className="mt-5">
                    <p className="text-sm font-normal text-center text-mp-foreground">
                        <Link to="/login" className="text-mp-link">
                            Back to Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
